import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';

import { zodResolver } from '@hookform/resolvers/zod';
import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useOffers from 'api/useOffers';
import { useProduct } from 'api/useProduct';
import {
  EFeatureFlag,
  EPopupSubType,
  ESaleDiscountDisplayType,
  OfferType
} from 'constants/enums';
import Decimal from 'decimal.js';
import { OfferFormLayout } from 'views/Offers/components/OfferFormLayout';
import { useOfferDesignOptions } from 'views/Offers/hooks/useOfferDesignOptions';
import { useOfferFormSubmit } from 'views/Offers/hooks/useOfferFormSubmit';
import { DEFAULT_OFFER_DESIGN_EXTERNAL_ID } from 'views/Offers/Offer.consts';
import { checkUniqueness } from 'views/Offers/Offer.utils';
import PreviewSection from 'views/Offers/Popups/Form/components/Sections/PreviewSection';
import { usePopupProducts } from 'views/Offers/Popups/Form/hooks/usePopupProducts';
import { OfferDesignTypes } from 'views/OffersUI/OffersUIForm/types';

import { Form } from 'components/ui';

import { Offer, PopUpProductSequence, PopUpSequenceProduct } from 'common/contracts';
import { v4 as uuidv4 } from 'uuid';

import { DailyBonusContent } from './components/DailyBonusContent';
import {
  DAILY_BONUS_DEFAULT_VALUES,
  DAILY_BONUS_SCHEMA,
  DailyBonusFormFields,
  LOCKED_STATE_BUTTON_TEXT_DEFAULT
} from './DailyBonusForm.consts';
import { ProductEntry } from '../../Popups/Form/hooks/usePopupProducts';

interface IDailyBonusFormProps {
  edit?: boolean;
  dup?: boolean;
  showLockedStateButtonText?: boolean;
}

const DailyBonusForm: FC<IDailyBonusFormProps> = ({
  edit = false,
  dup = false
}) => {
  const { publisherId } = useParams();
  const { featureFlags, isLoading } = useFeatureFlags({ publisherId });

  if (isLoading) {
    return null;
  }

  if (!featureFlags?.[EFeatureFlag.DASHBOARD_DAILY_BONUS_SIDE_MENU]) {
    return <Navigate to="../popups" replace />;
  }

  const showLockedStateButtonText = !!featureFlags?.[EFeatureFlag.DASHBOARD_SHOW_COLLECT_TEXT_PRIOR_COUNTDOWN];

  return <DailyBonusFormContent edit={edit} dup={dup} showLockedStateButtonText={showLockedStateButtonText} />;
};

const DailyBonusFormContent: FC<IDailyBonusFormProps> = ({
  edit = false,
  dup = false,
  showLockedStateButtonText = false
}) => {
  const { publisherId, popupId } = useParams();
  const navigate = useNavigate();
  const { getProducts } = useProduct(publisherId);
  const {
    addPopUpOffer,
    updatePopUpOffer,
    getOffer,
    getOffers,
    formatProductQuantity
  } = useOffers({ offerId: popupId, offerType: OfferType.POPUP });

  const { submitOffer } = useOfferFormSubmit({
    edit,
    dup,
    offerLabel: 'Daily Bonus',
    addOffer: addPopUpOffer,
    updateOffer: updatePopUpOffer,
    onSuccess: (): void => {
      getOffers.refetch();
    }
  });

  const { getImages } = useImages(publisherId);
  const { offerDesignOptions, defaultOfferDesignId: defaultOfferUiId } =
    useOfferDesignOptions({
      publisherId: publisherId || '',
      offerDesignType: OfferDesignTypes.PopUp,
      defaultExternalId: DEFAULT_OFFER_DESIGN_EXTERNAL_ID[OfferType.POPUP]
    });

  const [activeTab, setActiveTab] = useState('settings');

  const offers = useMemo(
    () => getOffers.data?.offers || [],
    [getOffers.data?.offers]
  );
  const currentOffer = getOffer.data?.result;

  const form = useForm<DailyBonusFormFields>({
    resolver: zodResolver(DAILY_BONUS_SCHEMA),
    mode: 'onChange',
    defaultValues: DAILY_BONUS_DEFAULT_VALUES
  });

  const {
    productsList,
    addProduct,
    deleteProduct,
    updateProduct,
    reorderProducts,
    setMultiProducts
  } = usePopupProducts(false);

  const isNameTaken = !checkUniqueness(
    form.watch('name'),
    'name',
    offers,
    currentOffer,
    edit
  );
  const isPublisherOfferIdTaken = !checkUniqueness(
    form.watch('publisherOfferId'),
    'publisherOfferId',
    offers,
    currentOffer,
    edit
  );

  useEffect(() => {
    form.register('products');
  }, [form, form.register]);

  const currentOfferUI = useWatch({
    control: form.control,
    name: 'offerUiId',
    defaultValue: defaultOfferUiId
  });

  useEffect(() => {
    if (!popupId && defaultOfferUiId) {
      form.setValue('offerUiId', defaultOfferUiId, {
        shouldDirty: false,
        shouldValidate: true
      });
    }

    if ((edit || dup) && currentOffer) {
      const products: ProductEntry[] = (
        currentOffer.productsSequence as PopUpProductSequence[]
      )
        .filter((seq) => seq.products.length > 0)
        .map((seq) => {
          const product = seq.products[0] as PopUpSequenceProduct;
          return {
            id: uuidv4(),
            productId: product.product?.productId ?? '',
            quantity: product.quantity ?? 0,
            displayName: product.product?.displayName ?? '',
            image: product.product?.images?.[0]?.url ?? '',
            quantityDisplay: product?.quantityDisplay
          };
        });

      setMultiProducts(products);

      form.reset({
        ...DAILY_BONUS_DEFAULT_VALUES,
        ...currentOffer,
        active: dup || edit ? currentOffer.active : true,
        name: dup ? `${currentOffer.name}_copy` : currentOffer.name,
        displayName: `${dup ? currentOffer.displayName ?? currentOffer.name : currentOffer.displayName ?? currentOffer.name}${dup ? '_copy' : ''}`,
        publisherOfferId: dup
          ? `${currentOffer.publisherOfferId}_copy`
          : currentOffer.publisherOfferId,
        offerUiId:
          currentOffer.offerUi?._id ||
          currentOffer.offerUi?.offerUiId ||
          defaultOfferUiId,
        priority: currentOffer.priority ?? null,
        subType: EPopupSubType.DAILY_BONUS,
        startOver: currentOffer.hasOwnProperty('startOver')
          ? currentOffer.startOver
          : true,
        lockedStateButtonText:
          currentOffer.lockedStateButtonText ??
          currentOffer.offerUi?.lockedStateButtonText ??
          LOCKED_STATE_BUTTON_TEXT_DEFAULT,
        products
      } as DailyBonusFormFields);
    }
  }, [edit, dup, currentOffer, form, popupId, defaultOfferUiId, setMultiProducts]);

  const onSubmit = useCallback(
    (data: DailyBonusFormFields): void => {
      const productsSequence = productsList.map((product, index) => ({
        index: index + 1,
        priceInUsdCents: 0,
        products: [
          {
            productId: product.productId,
            quantity: product.quantity
          }
        ]
      }));

      const payload: Partial<Offer> & { lockedStateButtonText?: string } = {
        active: data.active,
        coolDownInHours: 1,
        ...(data.description && { description: data.description }),
        displayName: data.displayName || data.name,
        name: data.name,
        offerUiId: data.offerUiId,
        priceDiscount: {
          discount: 0,
          type: ESaleDiscountDisplayType.PERCENTAGE
        },
        priority: data.priority ?? undefined,
        productsSequence:
          productsSequence.length > 0
            ? productsSequence
            : [{ index: 1, priceInUsdCents: 0, products: [] }],
        publisherOfferId: data.publisherOfferId,
        segments: data.segments,
        showAfter: 'login',
        startOver: data.startOver,
        subType: EPopupSubType.DAILY_BONUS,
        tags: [],
        triggers: [{ type: 'time', eventName: 'impression', every: 1440 }],
        type: OfferType.POPUP,
        ...(showLockedStateButtonText && { lockedStateButtonText: data.lockedStateButtonText ?? '' })
      };
      submitOffer(payload, popupId);
    },
    [productsList, submitOffer, popupId]
  );

  const watchedName = form.watch('name');

  const formTitle = useMemo((): string => {
    if (edit) {
      return `Edit Daily Bonus (${watchedName || currentOffer?.name || ''})`;
    }
    if (dup) {
      return 'Duplicate Daily Bonus';
    }
    return 'Create Daily Bonus';
  }, [edit, dup, watchedName, currentOffer?.name]);

  const isFormValid = useMemo(
    (): boolean =>
      form.formState.isValid &&
      (dup || form.formState.isDirty) &&
      !isNameTaken &&
      !isPublisherOfferIdTaken,
    [
      form.formState.isValid,
      form.formState.isDirty,
      dup,
      isNameTaken,
      isPublisherOfferIdTaken
    ]
  );

  const handleSave = useCallback((): void => {
    form.handleSubmit(onSubmit)();
  }, [form, onSubmit]);

  const handleClose = useCallback((): void => {
    navigate('../');
  }, [navigate]);

  let buttonText = 'Save';
  if (edit) {
    buttonText = 'Update';
  }

  const isImagesReady = !getImages.isLoading && !!getImages.data;

  return (
    <OfferFormLayout
      title={formTitle}
      mainButtonText={buttonText}
      isFormValid={isFormValid}
      onSave={handleSave}
      onClose={handleClose}
      previewContent={
        <PreviewSection
          publisherId={publisherId}
          currentOfferUI={currentOfferUI}
          isDailyBonus
          currentPrice={0}
          products={getProducts.data}
          productsList={productsList}
        />
      }
    >
      {isImagesReady && (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mt-7 mb-9 w-full px-24 flex flex-col gap-y-9"
          >
            <DailyBonusContent
              form={form}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              offerDesignOptions={offerDesignOptions}
              isNameTaken={isNameTaken}
              isPublisherOfferIdTaken={isPublisherOfferIdTaken}
              getProducts={getProducts}
              productsList={productsList}
              addProduct={addProduct}
              deleteProduct={deleteProduct}
              updateProduct={updateProduct}
              reorderProducts={reorderProducts}
              formatProductQuantity={formatProductQuantity}
              showLockedStateButtonText={showLockedStateButtonText}
            />
          </form>
        </Form>
      )}
    </OfferFormLayout>
  );
};

export { DailyBonusForm };
